import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Loader2, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

const btn = "inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-4 py-2 transition-colors disabled:opacity-60";
const TYPES = [["text", "Tekst (jedna linia)"], ["textarea", "Tekst wielowierszowy"], ["date", "Data"], ["select", "Lista wyboru"]];
const NEW = { key: "", label: "", label_en: "", type: "text", required: false, options: [] };

function FieldRow({ f, i, count, onChange, onMove, onRemove }) {
  return (
    <div data-testid={`order-field-${i}`} className="rounded-xl border border-border p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
          Pole {i + 1}{f.label.trim() && <> · znacznik <code className="text-foreground normal-case">{`{${f.label.trim()}}`}</code></>}
        </span>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => onMove(-1)} disabled={i === 0} className="p-1.5 rounded-full hover:bg-muted disabled:opacity-30" aria-label="W górę"><ArrowUp size={13} /></button>
          <button type="button" onClick={() => onMove(1)} disabled={i === count - 1} className="p-1.5 rounded-full hover:bg-muted disabled:opacity-30" aria-label="W dół"><ArrowDown size={13} /></button>
          <button type="button" data-testid={`order-field-delete-${i}`} onClick={onRemove} className="p-1.5 rounded-full hover:bg-destructive/10 text-destructive" aria-label="Usuń"><Trash2 size={13} /></button>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Input data-testid={`order-field-label-${i}`} value={f.label} onChange={(e) => onChange({ label: e.target.value })} placeholder="Etykieta (PL) *, np. PESEL" className="bg-background" />
        <Input data-testid={`order-field-label-en-${i}`} value={f.label_en || ""} onChange={(e) => onChange({ label_en: e.target.value })} placeholder="Label (EN, opcjonalnie)" className="bg-background" />
        <select data-testid={`order-field-type-${i}`} value={f.type} onChange={(e) => onChange({ type: e.target.value })} className="h-10 rounded-md border border-input bg-background px-3 text-sm">
          {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <label className="flex items-center gap-2 text-xs font-semibold h-10">
          <Switch data-testid={`order-field-required-${i}`} checked={!!f.required} onCheckedChange={(v) => onChange({ required: v })} /> Obowiązkowe
        </label>
      </div>
      {f.type === "select" && (
        <Input data-testid={`order-field-options-${i}`} value={(f.options || []).join(" | ")} onChange={(e) => onChange({ options: e.target.value.split("|").map((o) => o.trimStart()) })}
          placeholder="Opcje oddzielone | , np. Tak | Nie | Nie wiem" className="bg-background" />
      )}
    </div>
  );
}

export function OrderFieldsEditor({ onSaved }) {
  const [fields, setFields] = useState(null);
  const [saved, setSaved] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/order-fields").then((r) => { setFields(r.data.fields); setSaved(JSON.stringify(r.data.fields)); }).catch(() => toast.error("Nie udało się pobrać pól formularza."));
  }, []);
  if (!fields) return <div className="flex justify-center py-8"><Loader2 className="animate-spin text-slate-600" /></div>;

  const dirty = JSON.stringify(fields) !== saved;
  const update = (i, patch) => setFields(fields.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  const move = (i, d) => {
    const next = [...fields];
    const [it] = next.splice(i, 1);
    next.splice(i + d, 0, it);
    setFields(next);
  };
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/order-fields", { fields: fields.map((f) => ({ ...f, options: (f.options || []).map((o) => o.trim()).filter(Boolean) })) });
      setFields(r.data.fields);
      setSaved(JSON.stringify(r.data.fields));
      toast.success("Pola formularza zapisane.");
      onSaved?.();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Nie udało się zapisać pól.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="order-fields-editor" className="bg-card border border-border rounded-2xl p-5 sm:p-6 space-y-5">
      <div>
        <h2 className="font-display font-bold text-lg">Dodatkowe pola formularza zamówienia</h2>
        <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
          Każde pole pojawi się w formularzu „Zamów pakiet” na stronie (pod danymi kontaktowymi) i jako znacznik <code className="font-mono">{"{etykieta}"}</code> do wstawienia
          w szablonie umowy poniżej. Wartości trafiają do PDF umowy, e-maili, szczegółów zamówienia i eksportu CSV.
        </p>
      </div>
      {fields.length === 0 && <p data-testid="order-fields-empty" className="text-sm text-muted-foreground italic">Brak dodatkowych pól — formularz zawiera tylko standardowe dane.</p>}
      <div className="space-y-3">
        {fields.map((f, i) => (
          <FieldRow key={f.key || `new-${i}`} f={f} i={i} count={fields.length} onChange={(p) => update(i, p)} onMove={(d) => move(i, d)} onRemove={() => setFields(fields.filter((_, j) => j !== i))} />
        ))}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button type="button" data-testid="order-field-add-button" onClick={() => setFields([...fields, { ...NEW }])} disabled={fields.length >= 15} className={`${btn} border border-dashed border-border hover:bg-muted`}>
          <Plus size={13} /> Dodaj pole
        </button>
        <div className="flex items-center gap-3">
          {dirty && <span data-testid="order-fields-unsaved" className="text-xs text-amber-600 dark:text-amber-400">Niezapisane zmiany</span>}
          <button type="button" data-testid="order-fields-save-button" onClick={save} disabled={saving || !dirty} className={`${btn} bg-brand-teal text-white hover:bg-brand-tealDark`}>
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Zapisz pola
          </button>
        </div>
      </div>
    </div>
  );
}
